import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Badge } from '@/components/Badge'
import {
  analysisFlow,
  cockpitMainNavigation,
  cockpitPageDefinitions,
  overviewKpis,
  type CockpitKpi,
  type CockpitPageDefinition,
} from '@/features/cockpit/cockpitData'

function KpiCard({ kpi }: { readonly kpi: CockpitKpi }): ReactNode {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {kpi.label}
      </p>
      <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
        {kpi.value}
      </p>
      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{kpi.context}</p>
    </article>
  )
}

function CockpitAreaCard({
  definition,
}: {
  readonly definition: CockpitPageDefinition
}): ReactNode {
  const navItem = cockpitMainNavigation.find((item) => item.path === definition.path)

  return (
    <Link
      to={definition.path}
      aria-label={definition.title}
      className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-500"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
            {definition.eyebrow}
          </p>
          <h3 className="mt-1 text-lg font-bold text-slate-900 group-hover:text-blue-700 dark:text-slate-100 dark:group-hover:text-blue-300">
            {definition.title}
          </h3>
        </div>
        {navItem?.badge === undefined ? null : <Badge>{navItem.badge}</Badge>}
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
        {definition.description}
      </p>
      <div className="mt-4 flex items-center justify-between gap-3 text-xs">
        <span className="text-slate-500 dark:text-slate-400">
          {definition.subviews.length} Unteransichten vorbereitet
        </span>
        <span className="font-semibold text-blue-700 dark:text-blue-300">Bereich öffnen</span>
      </div>
    </Link>
  )
}

export function CockpitOverviewPage(): ReactNode {
  const pageDefinitions = Object.values(cockpitPageDefinitions)

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
              Weltmaschine Insight Cockpit
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Übersicht
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Zentrale Entscheidungsgrundlagen für Produkt- und Marketingteams. Die Übersicht ist
              der Einstieg in alle Analysebereiche.
            </p>
          </div>
          <Badge>Strukturvorschlag mit Demo-Daten</Badge>
        </div>
      </header>

      <section
        aria-label="Hinweis zu Demo-Daten"
        className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
      >
        <h2 className="font-semibold">Erste Version als klickbarer Wireframe</h2>
        <p className="mt-1 max-w-5xl text-sm leading-6">
          Alle neuen Cockpit-Seiten zeigen bewusst gekennzeichnete Dummy-Inhalte. Die Oberfläche ist
          für die spätere Anbindung vorbereitet. Noch nicht verfügbare Datenquellen und Berechnungen
          werden direkt im jeweiligen Bereich benannt.
        </p>
      </section>

      <section aria-label="Cockpit-Kennzahlen" className="mb-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Schnellüberblick</h2>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Demo-Stand: 04.08.2026, 09:30 Uhr
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {overviewKpis.map((kpi) => (
            <KpiCard key={kpi.label} kpi={kpi} />
          ))}
        </div>
      </section>

      <section aria-label="Analysefluss" className="mb-8">
        <div className="mb-3">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Analysefluss</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Jeder Bereich folgt demselben Entscheidungsweg.
          </p>
        </div>
        <ol className="grid gap-3 md:grid-cols-4">
          {analysisFlow.map((step, index) => (
            <li
              key={step.id}
              className="relative rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
              <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
                0{index + 1}
              </span>
              <h3 className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {step.label}
              </h3>
              <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section aria-label="Cockpit-Bereiche" className="mb-8">
        <div className="mb-3">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Cockpit-Bereiche</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Die sechs Hauptbereiche bleiben jederzeit über die Hauptnavigation erreichbar.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {pageDefinitions.map((definition) => (
            <CockpitAreaCard key={definition.id} definition={definition} />
          ))}
        </div>
      </section>

      <section
        aria-label="Weitere Bereiche"
        className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              Weitere Bereiche
            </h2>
            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Bestehende Arbeitsoberflächen bleiben neben dem neuen Cockpit verfügbar.
            </p>
          </div>
          <Badge>Bestehende Funktionen</Badge>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/stats"
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
          >
            Themen-Statistik
          </Link>
          <Link
            to="/sales"
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
          >
            Verkaufsanalyse
          </Link>
          <Link
            to="/topics"
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
          >
            Potenzial-Trichter
          </Link>
          <Link
            to="/orders"
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
          >
            Live-Verkäufe
          </Link>
        </div>
      </section>
    </div>
  )
}
